import type { QueryClient } from "@tanstack/react-query";
import { studioEnrollmentsQueryKey } from "@/features/enrollments/hooks/useStudioEnrollmentsGet";
import type { ScheduledDropEnrollmentItem } from "@/lib/dashboard/scheduled-drop-enrollments-widget";
import { QueryKeys } from "@/lib/types";
import { DashboardWidgetId } from "@/lib/types/dashboard-widgets";
import { formatDateTableDisplay } from "@/lib/utils/date/date";
import {
  EnrollmentStatus,
  type GetEnrollmentResponse,
} from "@/openapi/generated";

export type AdminScheduleDropCacheParams = {
  enrollmentId: number;
  dropDateYmd: string;
  studentId?: number;
  studentName?: string;
  className?: string;
  classId?: number | null;
};

function patchEnrollmentItems(
  items: GetEnrollmentResponse[],
  enrollmentId: number,
  patch: Partial<GetEnrollmentResponse>,
): GetEnrollmentResponse[] {
  return items.map((enrollment) =>
    Number(enrollment.enrollmentId) === enrollmentId
      ? { ...enrollment, ...patch }
      : enrollment,
  );
}

function upsertEnrollmentItems(
  items: GetEnrollmentResponse[],
  enrollmentId: number,
  patch: Partial<GetEnrollmentResponse>,
  seed?: GetEnrollmentResponse,
): GetEnrollmentResponse[] {
  const index = items.findIndex(
    (enrollment) => Number(enrollment.enrollmentId) === enrollmentId,
  );
  if (index >= 0) {
    const next = [...items];
    next[index] = { ...next[index], ...patch };
    return next;
  }
  if (!seed) return items;
  return [...items, { ...seed, ...patch }];
}

function upsertEnrollmentListData<T>(
  data: T,
  enrollmentId: number,
  patch: Partial<GetEnrollmentResponse>,
  seed?: GetEnrollmentResponse,
): T {
  if (Array.isArray(data)) {
    return upsertEnrollmentItems(data, enrollmentId, patch, seed) as T;
  }
  if (data && typeof data === "object" && "items" in data) {
    const paginated = data as { items: GetEnrollmentResponse[] };
    return {
      ...paginated,
      items: upsertEnrollmentItems(
        paginated.items ?? [],
        enrollmentId,
        patch,
        seed,
      ),
    } as T;
  }
  return data;
}

function buildScheduleDropPatch(
  dropDateYmd: string,
): Partial<GetEnrollmentResponse> {
  const now = new Date();
  return {
    dropDate: dropDateYmd,
    droppedAt: dropDateYmd,
    status: EnrollmentStatus.Dropped,
    updatedAt: now,
    statusChangedAt: now,
    activityHistory: [],
  };
}

function buildScheduleDropSeed(
  params: AdminScheduleDropCacheParams,
): GetEnrollmentResponse | undefined {
  if (params.studentId == null || params.studentId <= 0) return undefined;
  const name = params.studentName?.trim() || "Unknown Student";
  const [firstName, ...rest] = name.split(/\s+/);
  return {
    enrollmentId: params.enrollmentId,
    status: EnrollmentStatus.Dropped,
    enrolledAt: null,
    student: {
      studentId: params.studentId,
      firstName: firstName || "Unknown",
      lastName: rest.join(" "),
      isActive: true,
    },
    _class: {
      classId: params.classId ?? 0,
      name: params.className ?? "Class",
      description: null,
      isActive: true,
    },
  } as unknown as GetEnrollmentResponse;
}

function buildScheduledDropWidgetRow(
  params: AdminScheduleDropCacheParams,
): ScheduledDropEnrollmentItem {
  return {
    enrollmentId: params.enrollmentId,
    studentId: params.studentId ?? null,
    studentName: params.studentName?.trim() || "-",
    classId: params.classId ?? null,
    className: params.className?.trim() || "Class",
    dropDateYmd: params.dropDateYmd,
    dropDateLabel: formatDateTableDisplay(params.dropDateYmd),
  };
}

/** After canceling a scheduled drop, restore enrolled status and clear drop date. */
export function patchEnrollmentAfterScheduledDropCancel(
  queryClient: QueryClient,
  studentId: number,
  enrollmentId: number,
): void {
  queryClient.setQueriesData(
    { queryKey: [QueryKeys.ClassesEnrollmentsByStudentId, studentId] },
    (old) =>
      upsertEnrollmentListData(old as never, enrollmentId, {
        status: EnrollmentStatus.Enrolled,
        dropDate: null,
        droppedAt: null,
      } as Partial<GetEnrollmentResponse>),
  );
}

/** @deprecated Use patchEnrollmentAfterScheduledDropCancel */
export const patchEnrollmentAfterWithdrawalRejection =
  patchEnrollmentAfterScheduledDropCancel;

export type EnrollmentDropCacheParams = AdminScheduleDropCacheParams;

function buildImmediateDropPatch(
  dropDateYmd: string,
): Partial<GetEnrollmentResponse> {
  const now = new Date();
  return {
    status: EnrollmentStatus.Dropped,
    droppedAt: dropDateYmd,
    dropDate: null,
    updatedAt: now,
    statusChangedAt: now,
  };
}

/** Keeps dropped enrollments in the studio list for the status-changes feed after immediate withdraw. */
export function syncEnrollmentCachesAfterImmediateDrop(
  queryClient: QueryClient,
  params: EnrollmentDropCacheParams,
): void {
  const patch = buildImmediateDropPatch(params.dropDateYmd);
  const seed = buildScheduleDropSeed(params);

  if (params.studentId != null && params.studentId > 0) {
    queryClient.setQueriesData(
      { queryKey: [QueryKeys.ClassesEnrollmentsByStudentId, params.studentId] },
      (old) =>
        upsertEnrollmentListData(
          old as never,
          params.enrollmentId,
          patch,
          seed,
        ),
    );
  }

  queryClient.setQueriesData({ queryKey: studioEnrollmentsQueryKey }, (old) =>
    upsertEnrollmentListData(old as never, params.enrollmentId, patch, seed),
  );
}

/** Keeps dashboard feeds on "today" after admin schedules a future withdraw. */
export function syncEnrollmentCachesAfterAdminScheduleDrop(
  queryClient: QueryClient,
  params: AdminScheduleDropCacheParams,
): void {
  const patch = buildScheduleDropPatch(params.dropDateYmd);
  const seed = buildScheduleDropSeed(params);

  if (params.studentId != null && params.studentId > 0) {
    queryClient.setQueriesData(
      { queryKey: [QueryKeys.ClassesEnrollmentsByStudentId, params.studentId] },
      (old) =>
        upsertEnrollmentListData(
          old as never,
          params.enrollmentId,
          patch,
          seed,
        ),
    );
  }

  queryClient.setQueriesData({ queryKey: studioEnrollmentsQueryKey }, (old) =>
    upsertEnrollmentListData(old as never, params.enrollmentId, patch, seed),
  );

  const widgetRow = buildScheduledDropWidgetRow(params);
  queryClient.setQueriesData(
    {
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === QueryKeys.WidgetData &&
        query.queryKey.includes(DashboardWidgetId.ScheduledDropEnrollments),
    },
    (old: ScheduledDropEnrollmentItem[] | undefined) => {
      const items = old ?? [];
      const without = items.filter(
        (row) => row.enrollmentId !== params.enrollmentId,
      );
      return [...without, widgetRow];
    },
  );
}

/** @deprecated Use syncEnrollmentCachesAfterAdminScheduleDrop */
export function patchEnrollmentAfterAdminScheduleDrop(
  queryClient: QueryClient,
  params: AdminScheduleDropCacheParams,
): void {
  syncEnrollmentCachesAfterAdminScheduleDrop(queryClient, params);
}
